import React, { Component } from 'react';
import Slider from "react-slick";
import './account.scss';

import leftChev from '../../../assets/images/chev-left-royal.svg';
import rightChev from '../../../assets/images/chev-right-royal.svg';
import Button from '../../Form/Button/Button';
import AccountNs from './AccountNS';
import AccountOFS from './AccountOFS';
import AccountFrs from './AccountFRS';
import AccountFISA from './AccountFISA';
import PendingAccount from './PendingAccount';
import {joinClasses, findPos, sortArrayByObjectProperty} from '../../../utils/helpers';
import RolloverAccount from "./RolloverAccount";
import {productKeys, productTypes} from "../../../utils/productHelpers";
import withFeatureFlags from "../../FeatureFlags/withFeatureFlags";
import HowToFundPopup from "../HowToFundPopup/HowToFundPopup";
import {useUnifiedAnalytics} from "../../../contexts/AnalyticsWrapper";

/** @import {WithFeatureFlagsProps} from '../../FeatureFlags/FeatureFlagsTypes' */
/** @import {AccountsProps, AccountsState} from './AccountsTypes' */
/** @import { Account } from "../DashboardTypes" */
/** @import {PendingAccountData} from './PendingAccountTypes' */

/**
 * @typedef {Object} WithAnalyticsProps
 * @property {import('../../../contexts/AnalyticsWrapperTypes').UnifiedAnalytics} analytics
 */

const LARGE_BREAKPOINT_WIDTH = 1024;
let unlimitedDepositAccountTypes = [productTypes.EasyAccess, productTypes.InstantAccessCashISA, productTypes.Notice];

export const PrevArrow = (/** @type {{onClick?: React.MouseEventHandler<HTMLImageElement>; enabled?: boolean; visible?: boolean }} */ {onClick, enabled = true, visible = true}) => (
    <img
        src={leftChev}
        className={joinClasses('account-carousel__arrow', 'account-carousel__arrow--prev', visible ? '' : 'account-carousel__arrow--invisible')}
        onClick={enabled ? onClick : undefined}
    />
);
export const NextArrow = (/** @type {{onClick?: React.MouseEventHandler<HTMLImageElement>; enabled?: boolean; visible?: boolean }} */ {onClick, enabled = true, visible = true}) => (
    <img
        src={rightChev}
        className={joinClasses('account-carousel__arrow', 'account-carousel__arrow--next', visible ? '' : 'account-carousel__arrow--invisible')}
        onClick={enabled ? onClick : undefined}
    />
);

/** @extends {Component<AccountsProps & WithFeatureFlagsProps & WithAnalyticsProps, AccountsState>} */
class Accounts extends Component {
    /** @type {AccountsState} */
    state = {
        carouselEnabled: window.innerWidth < LARGE_BREAKPOINT_WIDTH,
        selectedAccountActivity: null,
        accountWithActionInProgress: null,
        popupOpen: false,
        popupEnabled: window.innerWidth >= LARGE_BREAKPOINT_WIDTH,
        popupSide: 'left',
        popupRender: () => (<>na</>),
        modalOpen: false
    };

    /** @type {React.RefObject<HTMLDivElement>} */
    activityRef = React.createRef();

    /** @type {React.RefObject<HTMLDivElement>} */
    popupRef = React.createRef();

    componentDidMount() {
        window.addEventListener('resize', this.onWindowResize, false);
        document.body.addEventListener('click', this.onClickOutside, false);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onWindowResize);
        document.body.removeEventListener('click', this.onClickOutside);
    }

    onWindowResize = () => {
        this.setState({
            carouselEnabled: window.innerWidth < LARGE_BREAKPOINT_WIDTH,
            popupEnabled: window.innerWidth >= LARGE_BREAKPOINT_WIDTH,
            popupOpen: false
        });
    }

    onClickOutside = (/** @type {MouseEvent} */ event) => {
        if (
            this.state.popupOpen
            && this.popupRef.current
            && window.getComputedStyle(this.popupRef.current).display !== 'none'
            && event.target instanceof Element
            && !this.popupRef.current.contains(event.target)
        ) {
            event.preventDefault();
            event.stopPropagation();
            this.setState({
                popupOpen: false,
                popupRender: () => (<></>)
            });
        }
    };

    toggleInfo = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event, /** @type {() => React.JSX.Element} */ renderContent) => {
        event.preventDefault();

        if (!this.state.popupEnabled || !event.currentTarget) return false;

            let button = event.currentTarget;
            let rect = button.getBoundingClientRect();
            let popupSide = 'left';
            let popupPos = {
                width: 400,
                top: window.scrollY + rect.top + 31,
                left: rect.left - 16,
            };
            if (rect.left > window.innerWidth / 2) {
                popupSide = 'right';
                popupPos = {
                    width: 400,
                    top: window.scrollY + rect.top + 31,
                    left: rect.left - 400 + 48
                };
            }

            this.setState(({popupOpen}) => ({
                popupOpen: !popupOpen,
                popupPos: popupPos,
                popupRender: renderContent,
                popupSide: popupSide
            }));

            return true;
    };

    selectAccountActivity = (/** @type {Account} */ account) => {
        this.setState(({selectedAccountActivity}) => {
            if (account === selectedAccountActivity) {
                return {selectedAccountActivity: null};
            } else {
                return {selectedAccountActivity: account};
            }
        }, () => {
            if (this.state.selectedAccountActivity && this.activityRef.current) {
                window.scroll(0, findPos(this.activityRef.current));
            }
        });
    }

    onActionComplete = (/** @type {Account} */ _account) => {
        this.setState({accountWithActionInProgress: null});
    }

    onActionStart = (/** @type {Account} */ account) => {
        this.setState({accountWithActionInProgress: account});
    }

    openNewAccountClick = (/** @type {string} */ url) => {
        if (this.props.analytics) {
            this.props.analytics.trackAnalyticsClickEvent(
                "open a new account",
                `${window.location.origin}/${url}`,
                "dashboard",
                "",
                "",
                {},
                "link click",
                "dashboard|click|open-new-account",
                'dashboard'
            );
        }
        window.location.href = url;
    };

    howToFundClick = () => {
        if (this.props.analytics) {
            this.props.analytics.trackAnalyticsClickEvent(
                "how to make a deposit",
                `${window.location.origin}`,
                "dashboard",
                "deposit-product",
                "",
                {},
                "link click",
                "dashboard|click|how to make a deposit",
                'Dashboard'
            );
        }
        this.onModalOpen();
    };

    onModalOpen = () => {
        this.setState({ modalOpen: true });

        // @ts-expect-error window.MSStream upsets TypeScript
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const body = document.body;

        if (isIOS) {
            //const scrollTop = window.pageYOffset;
            //body.style.position = 'fixed';
            //body.style.top = `-${scrollTop}px`;
            //body.style.width = '100%';
            //body.style.bottom = `-${scrollTop}px`;
        } else {
            body.style.overflow = 'hidden';
        }
    };

    onModalClose = () => {
        this.setState({ modalOpen: false });
        const body = document.body;
        body.style.height = '';
        body.style.overflowY = '';
        body.style.position = '';
    };

    getFundableAccountExists = () => {
        const { customer } = this.props;
        return customer.accounts.filter(a => ((a.accountStatus === "FundingOpen") || (unlimitedDepositAccountTypes.indexOf(a.product.productType) >= 0 && a.accountStatus !== "Closed"))).length > 0;
    };
    
    renderAccounts(/** @type {Account[]} */ accounts) {
        const {toAccount, customer} = this.props;
        const {selectedAccountActivity, accountWithActionInProgress} = this.state;
        
        const ofsAccounts = accounts.filter(acc => acc.product.productType === productTypes.EasyAccess);
        const noticeSaverAccounts = accounts.filter(acc => acc.product.productType === productTypes.Notice);
        const frsAccounts = accounts.filter(acc => acc.product.productType === productTypes.FixedTerm);
        const fisaAccounts = accounts.filter(acc => acc.product.productType === productTypes.FixedRateCashISA);
        const disaAccounts = this.props.featureEnabled('EnableFisaMaturity')
            ? accounts.filter(acc => acc.product.productType === productTypes.InstantAccessCashISA)
            : [];

        const frsIWOpenOrClosedAccounts = sortArrayByObjectProperty(
            frsAccounts.filter(acc => ["InstructionOpen", "InstructionClosed"].includes(acc.maturity ? acc.maturity.maturityInstructionWindowStatus : '')),
            ['maturity', 'maturityWindowCloseDate'],
            Date.parse
        );
        const frsOtherAccounts = frsAccounts.filter(acc => !["InstructionOpen", "InstructionClosed"].includes(acc.maturity ? acc.maturity.maturityInstructionWindowStatus : ''));

        const renderFrsIWOpenOrClosedAccountCards = (/** @type {Account} */ account) => {
            const accountIsRollingOver = (account.maturity && account.maturity.maturityInstruction.includes("Rollover") && account.accountStatus !== "Closed")
            const product = account.maturity && account.maturity.rolloverAccountProductId
                ? customer.products.find(product => account.maturity && (product.id === account.maturity.rolloverAccountProductId))
                : customer.products.find(product => product.code === productKeys.FixedRateSaver);

            return [
                <AccountFrs
                    activityRef={this.activityRef}
                    key={account.id}
                    account={account}
                    customer={customer}
                    toAccount={toAccount}
                    onSelectActivity={this.selectAccountActivity}
                    onActionComplete={this.onActionComplete}
                    onActionStart={this.onActionStart}
                    activityIsShowing={selectedAccountActivity === account}
                    hide={selectedAccountActivity && selectedAccountActivity !== account}
                    disable={accountWithActionInProgress && accountWithActionInProgress !== account}
                    accountWithActionInProgress={accountWithActionInProgress}
                    showPopup={this.toggleInfo}
                />,
                accountIsRollingOver &&
                    <RolloverAccount account={account} key={`${account.id}+rollover`} product={product}/>
            ]
        }

        return [frsIWOpenOrClosedAccounts.map(account => renderFrsIWOpenOrClosedAccountCards(account)),
            ofsAccounts.map(account =>
                // @ts-ignore
                <AccountOFS
                activityRef={this.activityRef}
                key={account.id}
                account={account}
                customer={customer}
                toAccount={toAccount}
                onSelectActivity={this.selectAccountActivity}
                onActionComplete={this.onActionComplete}
                onActionStart={this.onActionStart}
                activityIsShowing={selectedAccountActivity === account}
                hide={selectedAccountActivity && selectedAccountActivity !== account}
                disable={accountWithActionInProgress && accountWithActionInProgress !== account}
                accountWithActionInProgress={accountWithActionInProgress}
                showPopup={this.toggleInfo}
            />),
            disaAccounts.map(account =>
                <AccountOFS
                activityRef={this.activityRef}
                key={account.id}
                account={account}
                customer={customer}
                toAccount={toAccount}
                onSelectActivity={this.selectAccountActivity}
                onActionComplete={this.onActionComplete}
                onActionStart={this.onActionStart}
                activityIsShowing={selectedAccountActivity === account}
                hide={selectedAccountActivity && selectedAccountActivity !== account}
                disable={accountWithActionInProgress && accountWithActionInProgress !== account}
                accountWithActionInProgress={accountWithActionInProgress}
                showPopup={this.toggleInfo}
            />),
            noticeSaverAccounts.map(account => <AccountNs
                activityRef={this.activityRef}
                key={account.id}
                account={account}
                customer={customer}
                toAccount={toAccount}
                onSelectActivity={this.selectAccountActivity}
                onActionComplete={this.onActionComplete}
                onActionStart={this.onActionStart}
                activityIsShowing={selectedAccountActivity === account}
                hide={selectedAccountActivity && selectedAccountActivity !== account}
                disable={accountWithActionInProgress && accountWithActionInProgress !== account}
                accountWithActionInProgress={accountWithActionInProgress}
                showPopup={this.toggleInfo}/>),
            frsOtherAccounts.map(account => <AccountFrs
                activityRef={this.activityRef}
                key={account.id}
                account={account}
                customer={customer}
                toAccount={toAccount}
                onSelectActivity={this.selectAccountActivity}
                onActionComplete={this.onActionComplete}
                onActionStart={this.onActionStart}
                activityIsShowing={selectedAccountActivity === account}
                hide={selectedAccountActivity && selectedAccountActivity !== account}
                disable={accountWithActionInProgress && accountWithActionInProgress !== account}
                accountWithActionInProgress={accountWithActionInProgress}
                showPopup={this.toggleInfo}
            />),
            fisaAccounts.map(account => <AccountFISA
                activityRef={this.activityRef}
                key={account.id}
                account={account}
                customer={customer}
                toAccount={toAccount}
                onSelectActivity={this.selectAccountActivity}
                onActionComplete={this.onActionComplete}
                onActionStart={this.onActionStart}
                activityIsShowing={selectedAccountActivity === account}
                hide={selectedAccountActivity && selectedAccountActivity !== account}
                disable={accountWithActionInProgress && accountWithActionInProgress !== account}
                accountWithActionInProgress={accountWithActionInProgress}
                showPopup={this.toggleInfo}
            />)
        ]
    }

    renderPendingAccounts() {
        const { pendingAccounts, customer } = this.props;
        const visiblePendingAccounts = this.props.featureEnabled('EnableFisaMaturity')
            ? pendingAccounts
            : pendingAccounts?.filter(pa => {
                const product = customer.products.find(item => item.id === pa.productId);
                return !product || product.productType !== productTypes.InstantAccessCashISA;
            });
        /** @returns {PendingAccountData[]} */
        const mapPendingAccountsData = (/** @type {typeof this.props.pendingAccounts} */ pendingAccounts) => {
            if (!pendingAccounts || pendingAccounts.length === 0)
                return [];

            return pendingAccounts.map(pa => {
                const product = customer.products.find(obj => {
                    return obj.id === pa.productId;
                });
                return (product)
                    ? {
                        productName: product.name,
                        issueNumber: product.code === productKeys.NoticeSaver ? product.issueNumber : undefined // TODO: revisit when typechecking
                    }
                    : undefined;
            }).filter(pa => pa !== undefined);
        };

        return mapPendingAccountsData(visiblePendingAccounts).map((account,i) => {
            return (
                <PendingAccount
                    key={i}
                    account={account}
                />
            )
        });
    }

    renderCarouselOrNot() {
        const {accounts} = this.props;
        const {selectedAccountActivity, accountWithActionInProgress, carouselEnabled} = this.state;
        let canSwipe = selectedAccountActivity === null && accountWithActionInProgress === null;
        let arrowsVisible = accountWithActionInProgress === null;
        let initialSlide = selectedAccountActivity ? accounts.indexOf(selectedAccountActivity) : 0;

        const inactiveAccounts = accounts.filter(acc => acc.accountStatus === "Closed");
        const activeAccounts = accounts.filter(acc => acc.accountStatus !== "Closed");

        if (carouselEnabled) {
            return (
                <Slider
                    className="accounts__carousel account-carousel"
                    nextArrow={<NextArrow enabled={canSwipe} visible={arrowsVisible}/>}
                    prevArrow={<PrevArrow enabled={canSwipe} visible={arrowsVisible}/>}
                    swipe={canSwipe}
                    swipeToSlide={canSwipe}
                    draggable={canSwipe}
                    touchMove={canSwipe}
                    initialSlide={initialSlide}
                >
                    {this.renderAccounts(activeAccounts)}
                    {this.renderPendingAccounts()}
                    {this.renderAccounts(inactiveAccounts)}
                </Slider>
            )
        } else {
            return (<>
                {this.renderAccounts(activeAccounts)}
                {this.renderPendingAccounts()}
                {this.renderAccounts(inactiveAccounts)}
            </>);
        }
    }

    render() {
        const fundableAccountExists = this.getFundableAccountExists();
        
        return (
            <div className="accounts">
                <div className="accounts__header">
                    <h2 className="accounts__title">My Accounts</h2>
                    <div className="accounts__buttons">
                        {fundableAccountExists && 
                            <Button variant='tertiary' size='med' label='How to make a deposit' customClassNames={["dashboard__open-another-account__button"]}
                                    onClick={() => this.howToFundClick()} /> 
                        }

                        {this.props.featureEnabled('EnableSecondaryAccounts') && 
                            <Button variant='tertiary' size='med' label='Open another account' customClassNames={["dashboard__open-another-account__button"]} 
                                    onClick={() => this.openNewAccountClick('new-account')} />
                        }
                    </div>

                </div>
                
                {this.renderCarouselOrNot()}
                {this.state.popupOpen && this.state.popupEnabled && (
                    <div ref={this.popupRef}
                         className={['account__info-popup', 'is-open', this.state.popupSide].join(' ')}
                         style={this.state.popupPos}>
                        {this.state.popupRender()}
                    </div>
                )}
                
                {this.state.modalOpen && <HowToFundPopup data={{customer: this.props.customer}} onClose={this.onModalClose}/>}
            </div>
        );
    }
};

/**
 * Higher-order component that injects analytics
 * @param {React.ComponentType<any>} Component - Component that needs analytics
 * @returns {React.ComponentType<any>} - Wrapped component with analytics injected
 */
const WithAnalytics = (Component) => {
    /**
     * @param {Object} props - Component props without analytics
     * @returns {JSX.Element}
     */
    return function WrappedComponent(props) {
        const analytics = useUnifiedAnalytics();
        return <Component {...props} analytics={analytics} />;
    }
};

export default WithAnalytics(withFeatureFlags(Accounts));
